import { Component, inject, effect, output, input, OnInit, ViewChild, signal, ChangeDetectorRef, DestroyRef, Injector, afterNextRender, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import { CommonModule, registerLocaleData } from '@angular/common';
import { AddClaimService } from './add-claim.service';
import localeEs from '@angular/common/locales/es';
import { LOCALE_ID } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CatalogService } from '@services/data/catalog.service';
import { ThirdPartyFormComponent } from '../../components/third-party-form/third-party-form.component';
import { ClaimDetailsManagerComponent } from '../../components/claim-details-manager/claim-details-manager.component';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { areAllDetailsFinalized, countPendingDetails, getDetailEvidenceProgress, labelForClaimType, sanitizeNumericValue } from '../../utils/claim-details.util';
import { daysSince, reportMaxDays } from '../../utils/claim-process-rules.util';
import { forkJoin } from 'rxjs';
import { FormMode } from 'app/core/constants/form-mode';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import { TimelineComponent } from 'app/core/components/timeline/timeline.component';
import { FlowBadgeComponent } from 'app/core/components/flow-badge/flow-badge.component';
import { BpmService } from '../../../management-request/services/bpm.service';
import { BpmComponent } from '../../../management-request/components/bpm/bpm.component';
import { StepApprovalPayload } from '../../../management-request/components/step-approval-modal/step-approval-modal.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { SheetSelectComponent } from 'app/core/components/sheet-select/sheet-select.component';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';
import { SocketService } from '@services/core/socket.service';
import { filter } from 'rxjs/operators';

registerLocaleData(localeEs);

@Component({
  selector: 'app-add-claim',
  templateUrl: './add-claim.component.html',
  styleUrls: ['./add-claim.component.css'],
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule,
    ThirdPartyFormComponent, ClaimDetailsManagerComponent,
    PartyFormComponent, TimelineComponent, FlowBadgeComponent, BpmComponent,
    MobileFooterComponent, SheetSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AddClaimService, { provide: LOCALE_ID, useValue: 'es-ES' }]
})
export class AddClaimComponent implements OnInit {
  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
  });

  onClose = output<boolean>();
  claimAdded = output<void>();
  editorTitleChange = output<string | null>();
  headerEditorActionChange = output<'save' | 'finalize' | 'next' | null>();
  partyInput = input<any>(null);
  vehiclesInput = input<any[]>([]);
  claimUuidInput = input<string | null>(null);
  /** Solicitud de la bandeja (DRAFT) para desechar aunque claim.request venga incompleto. */
  requestInput = input<any>(null);

  @ViewChild(ClaimDetailsManagerComponent) claimFormComponent!: ClaimDetailsManagerComponent;

  private addClaimService = inject(AddClaimService);
  private catalogService = inject(CatalogService);
  private formBuilder = inject(FormBuilder);
  public bpmService = inject(BpmService);
  public appSettings = inject(AppSettings);
  private imageOptimizer = inject(ImageOptimizerService);
  isSaving = signal<boolean>(false);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private md = inject(MasterDetailNav);
  private socketService = inject(SocketService);
  private syncingDamageRoute = false;

  selectedDetailView = signal<any>(null);
  isEditingDetail = signal<boolean>(false);

  public selectedVehicleType = 'sedan';
  public partsSelected: string[] = [];
  public claimTypes: any[] = [];
  public capturedImage3D: string | null = null;
  // El flujo sin tercero salta del paso 1 al 3: el 2 solo existe para el afectado
  protected readonly STEP_PARTY = 1;
  protected readonly STEP_THIRD_PARTY = 2;
  protected readonly STEP_CLAIM = 3;

  public claimStep = this.STEP_PARTY;
  public createdClaim: any;
  public isLoadingClaim = false;

  public partyForm!: FormGroup;
  public claimForm!: FormGroup;
  public formMode = FormMode;

  // Lista en memoria de los daños menores acumulados
  public claimDetails: any[] = [];

  get thirdPartyBrands(): any[] {
    return this.catalogService.brands() ?? [];
  }

  get allModels(): any[] {
    return this.catalogService.models() ?? [];
  }

  get isThirdPartyClaim(): boolean {
    const type = this.claimForm?.get('claimType')?.value || this.createdClaim?.claimType;
    return type === 'ACCIDENT_THIRD_PARTY';
  }

  get claimTypeLabel(): string {
    const type = this.claimForm?.get('claimType')?.value || this.createdClaim?.claimType;
    return this.claimTypes.find(t => t.id === type)?.name || labelForClaimType(type);
  }

  get vehicleSelectItems(): any[] {
    return (this.vehiclesInput() ?? []).map((v) => {
      const extra = [v.brand?.name, v.model?.name].filter(Boolean).join(' ');
      return {
        uuid: v.uuid ?? v.id,
        selectLabel: extra ? `${v.plate} - ${extra}` : (v.plate ?? '')
      };
    });
  }

  get isEditableClaim(): boolean {
    if (this.claimUuidInput() && !this.createdClaim) return false;
    if (!this.createdClaim) return true;
    if (!this.createdClaim.requestId) return true;
    const status = String(
      this.createdClaim.request?.statusSlug
      || this.requestInput()?.statusSlug
      || ''
    ).toUpperCase();
    return status === 'DRAFT' || !status;
  }

  /**
   * Request BPM del siniestro (DRAFT). app-bpm necesita processSlug + statusSlug.
   */
  get activeRequest(): any | null {
    const fromInput = this.requestInput();
    const claim = this.createdClaim;
    const req = claim?.request ?? fromInput;
    if (req?.uuid) {
      return {
        ...req,
        processSlug: req.processSlug || claim?.claimType || this.claimForm?.get('claimType')?.value,
        statusSlug: req.statusSlug || fromInput?.statusSlug || 'DRAFT'
      };
    }
    return fromInput?.uuid ? { ...fromInput, statusSlug: fromInput.statusSlug || 'DRAFT' } : null;
  }

  get isDraftRequest(): boolean {
    return this.bpmService.isDraft(this.activeRequest)
      || (this.isEditableClaim && !!(this.createdClaim?.requestId || this.requestInput()?.uuid));
  }

  get bpmBlockedReason(): string | null {
    if (!this.isDraftRequest && !this.isEditableClaim) return null;
    const reason = this.submitBlockedReason();
    return reason || null;
  }

  /** Desktop: BPM (Enviar + Desechar) vive en el step-footer del paso siniestro. */
  get showClaimBpmDesktop(): boolean {
    return !this.appSettings.isMobile()
      && this.claimStep === this.STEP_CLAIM
      && this.claimDetails.length > 0
      && this.isEditableClaim
      && !!this.activeRequest;
  }

  /** Mobile: solo alerts del BPM; botones van en mobile-footer. */
  get showClaimBpmMobile(): boolean {
    return this.appSettings.isMobile()
      && this.claimStep === this.STEP_CLAIM
      && this.claimDetails.length > 0
      && this.isEditableClaim
      && !!this.activeRequest;
  }

  get steps(): any[] {
    return this.isThirdPartyClaim ? [
      { id: 1, title: 'Socio' },
      { id: 2, title: 'Afectado' },
      { id: 3, title: 'Siniestro y Envío' }
    ] : [
      { id: 1, title: 'Socio y tipo de siniestro' },
      { id: 2, title: 'Resumen del Siniestro y Envío' }
    ];
  }

  constructor() {
    effect(() => {
      if (this.partyForm && this.partyInput()) {
        this.partyForm.patchValue(this.partyInput());
      }
    });

    effect(() => {
      this.catalogService.processes();
      this.loadClaimTypes();
    });

    effect(() => {
      const title = this.resolveEditorTitle();
      queueMicrotask(() => {
        this.editorTitleChange.emit(title);
        this.headerEditorActionChange.emit(this.headerWizardAction());
      });
    });

    effect(() => {
      const editing = this.isEditingDetail();
      const det = this.selectedDetailView();
      queueMicrotask(() => this.syncDamageUrl(editing, det));
    });

    this.md.urlChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.applyDamageRoute());
  }

  private emitHeaderAction() {
    this.headerEditorActionChange.emit(this.headerWizardAction());
  }

  closeDetailEditor(): void {
    const ids = this.claimNavIds();
    const st = this.md.parse();
    this.claimFormComponent?.closeDetailEditor();
    this.isEditingDetail.set(false);
    if (st.area === 'socios' && ids.partyUuid && st.dano) {
      this.md.go(this.md.claim(ids.partyUuid, ids.claimUuid));
    }
  }

  registerDamageFromFooter(): void {
    this.openDamageRoute();
  }

  private maybeOpenFirstDamage(): void {
    if (!this.isEditableClaim || this.claimDetails.length > 0 || this.isEditingDetail()) return;
    afterNextRender(() => this.openDamageRoute(), { injector: this.injector });
  }

  private claimNavIds() {
    return {
      partyUuid: this.partyInput()?.uuid as string | null,
      claimUuid: (this.createdClaim?.uuid ?? this.claimUuidInput() ?? 'nuevo') as string
    };
  }

  private openDamageRoute(detailUuid?: string | null) {
    const { partyUuid, claimUuid } = this.claimNavIds();
    if (!partyUuid) {
      this.openCreateEditor();
      return;
    }
    const st = this.md.parse();
    if (st.dano && (st.detailUuid || null) === (detailUuid || null)) {
      this.applyDamageRoute();
      return;
    }
    this.md.go(this.md.damage(partyUuid, claimUuid, detailUuid));
  }

  private syncDamageUrl(editing: boolean, det: any) {
    if (this.syncingDamageRoute) return;
    const { partyUuid, claimUuid } = this.claimNavIds();
    if (!partyUuid) return;
    const st = this.md.parse();
    if (st.area !== 'socios' || st.view !== 'siniestro') return;
    const detailUuid = det?.uuid || det?.id || null;
    if (editing && !st.dano) {
      this.md.go(this.md.damage(partyUuid, claimUuid, detailUuid));
    } else if (!editing && st.dano) {
      this.md.go(this.md.claim(partyUuid, claimUuid));
    }
  }

  private applyDamageRoute() {
    const st = this.md.parse();
    if (st.area !== 'socios' || st.view !== 'siniestro') return;
    this.syncingDamageRoute = true;
    try {
      if (st.dano && !this.isEditingDetail()) this.openDamageFromRoute(st.detailUuid);
      if (!st.dano && this.isEditingDetail()) this.claimFormComponent?.closeDetailEditor();
    } finally {
      queueMicrotask(() => { this.syncingDamageRoute = false; });
    }
  }

  private openDamageFromRoute(detailUuid?: string | null) {
    if (detailUuid) {
      const found = this.claimDetails.find((d) => String(d.uuid || d.id) === String(detailUuid));
      if (found) {
        this.claimFormComponent?.openDetailView(found, { skipTimeframeCheck: true });
        this.emitHeaderAction();
        return;
      }
    }
    this.openCreateEditor();
  }

  saveEditorFromHeader(): void {
    const action = this.headerWizardAction();
    if (action === 'next') {
      this.nextClaimStep();
      return;
    }
    this.claimFormComponent?.runHeaderEditorAction();
  }

  /** Solo guarda el detalle abierto (sin dejarlo en revisión). */
  saveDetailDraftFromHeader(): void {
    const det = this.claimFormComponent?.selectedDetailView();
    if (det) this.claimFormComponent?.saveModalDetailChanges(det);
  }

  headerWizardAction(): 'save' | 'finalize' | 'next' | null {
    if (this.isLoadingClaim || this.isEditingDetail()) {
      return this.claimFormComponent?.headerEditorAction() ?? null;
    }
    if (this.claimStep === this.STEP_PARTY || this.claimStep === this.STEP_THIRD_PARTY) {
      return 'next';
    }
    return this.claimFormComponent?.headerEditorAction() ?? null;
  }

  isEditorSaving(): boolean {
    return this.isSaving() || !!(this.claimFormComponent?.isSavingDetail() || this.claimFormComponent?.isSavingNewDetail());
  }

  canFinalizeFromHeader(): boolean {
    return this.headerWizardAction() === 'finalize';
  }

  canDeleteFromHeader(): boolean {
    return this.isEditableClaim && this.isDraftRequest && !!this.activeRequest && !this.isEditingDetail();
  }

  canSubmitClaimFromFooter(): boolean {
    return !this.isEditingDetail() && !this.isLoadingClaim
      && this.claimStep === this.STEP_CLAIM
      && this.claimDetails.length > 0
      && this.isEditableClaim;
  }

  canRegisterDamage(): boolean {
    if (!this.isEditableClaim || this.isLoadingClaim || this.isEditingDetail()) return false;
    if (this.claimStep !== this.STEP_CLAIM) return false;
    const totalLoss = this.claimForm?.get('claimType')?.value === 'TOTAL_LOSS'
      || this.createdClaim?.claimType === 'TOTAL_LOSS';
    if (totalLoss && this.claimDetails.length >= 1) return false;
    if (this.claimFormComponent?.isFmrLimitReached) return false;
    return true;
  }

  private tryOpenFirstDamage(retries = 5): void {
    if (!this.isEditableClaim || this.claimDetails.length > 0 || this.isEditingDetail()) return;
    this.openCreateEditor(retries);
  }

  private openCreateEditor(retries = 5): void {
    if (this.isEditingDetail()) return;
    if (this.claimStep !== this.STEP_CLAIM || this.isLoadingClaim) {
      if (retries <= 0) return;
      setTimeout(() => this.openCreateEditor(retries - 1), 0);
      return;
    }
    if (!this.claimFormComponent) {
      if (retries <= 0) return;
      setTimeout(() => this.openCreateEditor(retries - 1), 0);
      return;
    }
    this.claimFormComponent.openCreateDetail();
    this.emitHeaderAction();
  }

  deleteFromHeader(): void {
    this.deleteRequestApproval();
  }

  deleteRequestApproval(): void {
    const uuid = this.activeRequest?.uuid;
    if (!uuid) return;
    Swal.fire({
      title: '¿Desechar borrador?',
      text: 'La solicitud quedará como Desechada en el historial y podrá reactivarse después. No se borran los datos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, desechar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.isSaving.set(true);
      Swal.fire({
        title: 'Desechando borrador...',
        text: 'Por favor, espera',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });
      this.addClaimService.discardRequest(uuid).subscribe({
        next: () => {
          Swal.close();
          Swal.fire({
            icon: 'success',
            title: 'Borrador desechado',
            text: 'Quedó en el historial como Desechada. Puedes reactivarla si hace falta.',
            timer: 2200,
            showConfirmButton: false
          }).then(() => this.onClose.emit(true));
        },
        error: (err) => {
          Swal.close();
          const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo desechar la solicitud.';
          Swal.fire('Error', msg, 'error');
          this.isSaving.set(false);
        }
      });
    });
  }

  private resolveEditorTitle(): string | null {
    if (!this.isEditingDetail()) return null;
    const det = this.selectedDetailView();
    return (!det?.id && !det?.uuid) ? 'Registrar daño' : 'Evidencias del daño';
  }

  ngOnInit() {
    this._buildForms();
    this.loadClaimTypes();
    this.bindCatalogDocRefresh();
    if (this.claimUuidInput()) {
      this.claimStep = this.STEP_CLAIM;
      this.isLoadingClaim = true;
      this.addClaimService.getClaim(this.claimUuidInput()!).subscribe({
        next: (claim) => {
          this.createdClaim = claim;
          this.claimDetails = claim.details || [];
          this._fillFormsFromClaim(claim);
          this.claimStep = this.STEP_CLAIM;
          this.isLoadingClaim = false;
          this.emitHeaderAction();
          this.maybeOpenFirstDamage();
        },
        error: (err) => {
          this.isLoadingClaim = false;
          console.error('Error al cargar siniestro existente', err);
        }
      });
    } else {
      this._prefillFromInputs();
    }
  }

  /** Sync applyToOpen: recarga document_claim del siniestro (no catálogo vivo). */
  private bindCatalogDocRefresh(): void {
    this.socketService.onCatalogChanged().pipe(
      filter((ev) => this.shouldRefreshDocsFromCatalog(ev)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.refreshClaimData());
  }

  private shouldRefreshDocsFromCatalog(ev: { scopes?: string[]; applyToOpen?: boolean; processSlug?: string } | null): boolean {
    if (!ev || ev.applyToOpen !== true) return false;
    const scopes = (ev.scopes ?? []).map((s) => String(s).toLowerCase());
    const hit = !scopes.length
      || scopes.includes('all')
      || scopes.includes('documentrequirements')
      || scopes.includes('document-requirements');
    if (!hit) return false;
    if (!this.createdClaim?.uuid) return false;
    const processSlug = this.createdClaim?.claimType || this.createdClaim?.request?.processSlug;
    if (ev.processSlug && processSlug && String(ev.processSlug) !== String(processSlug)) return false;
    return true;
  }

  private _buildForms() {
    this.partyForm = this.formBuilder.group({
      uuid: [null], rut: [null], partyTypeCode: [null], names: [null],
      birthday: [null], mail: [null], mobileNumber: [null], groupCode: [null], entityCode: [null]
    });
    this.claimForm = this.formBuilder.group({
      vehicleId: [null, Validators.required], claimType: [null, Validators.required],
      date: [null],
      driverName: [null], driverLicense: [null],
      driverLicenseClass: [null], accessoriesDescription: [null],
      geographicLocation: [null], laborCost: [0, [Validators.min(0)]],
      partsCost: [0, [Validators.min(0)]], totalCost: [0, [Validators.min(0)]],
      outOfCourtPayment: [0, [Validators.min(0)]], thirdPartyDriverName: [null],
      thirdPartyOwnerName: [null], thirdPartyLicense: [null], thirdPartyLicenseClass: [null],
      thirdPartyPhone: [null], thirdPartyPlate: [null], thirdPartyBrandCode: [null],
      thirdPartyModelCode: [null], thirdPartyYear: [null],
      thirdPartyHasInsurance: [false], thirdPartyTookResponsibility: [false]
    });

    this._registerClaimFormSubscriptions();
  }

  private _fillFormsFromClaim(claim: any) {
    this.claimForm.patchValue({
      vehicleId: claim.vehicle?.uuid || claim.vehicleId,
      claimType: claim.claimType,
      date: claim.dateOfLoss,
      driverName: claim.driverName,
      driverLicense: claim.driverLicense,
      driverLicenseClass: claim.driverLicenseClass,
      accessoriesDescription: claim.accessoriesDescription,
      geographicLocation: claim.geographicLocation,
      laborCost: claim.laborCost ?? 0,
      partsCost: claim.partsCost ?? 0,
      totalCost: claim.totalCost ?? 0,
      outOfCourtPayment: claim.outOfCourtPayment ?? 0,
    });
    
    if (claim.thirdPartyInfo) {
      const tp = claim.thirdPartyInfo;
      this.claimForm.patchValue({
        thirdPartyDriverName: tp.thirdPartyDriverName,
        thirdPartyOwnerName: tp.thirdPartyOwnerName,
        thirdPartyLicense: tp.thirdPartyLicense,
        thirdPartyLicenseClass: tp.thirdPartyLicenseClass,
        thirdPartyPhone: tp.thirdPartyPhone,
        thirdPartyPlate: tp.plate,
        thirdPartyBrandCode: tp.brandCode,
        thirdPartyModelCode: tp.modelCode,
        thirdPartyYear: tp.year,
        thirdPartyHasInsurance: tp.hasInsurance,
        thirdPartyTookResponsibility: tp.tookResponsibility,
      });
    }
    
    if (claim.parts) {
      this.partsSelected = Array.isArray(claim.parts)
        ? claim.parts
        : (() => { try { return JSON.parse(claim.parts || '[]'); } catch { return []; } })();
    }
  }

  private _registerClaimFormSubscriptions() {
    this.claimForm.get('vehicleId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => this._onClaimVehicleSelected(id));
    this.claimForm.get('claimType')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(type => this._onClaimTypeChanged(type));
  }

  private _onClaimVehicleSelected(id: any) {
    if (!id || !this.vehiclesInput()) return;
    const v = this.vehiclesInput()!.find(x => x.uuid === id || x.id === id);
    if (v && v.modelType) {
      this.selectedVehicleType = v.modelType;
    }
  }

  private _onClaimTypeChanged(type: string) {
    const isThird = type === 'ACCIDENT_THIRD_PARTY';
    const fields = [
      'thirdPartyDriverName', 'thirdPartyOwnerName', 'thirdPartyLicense', 'thirdPartyLicenseClass',
      'thirdPartyPhone', 'thirdPartyPlate', 'thirdPartyBrandCode',
      'thirdPartyModelCode', 'thirdPartyYear'
    ];
    fields.forEach(f => this._setControlValidity(f, isThird));
  }

  private _setControlValidity(field: string, isRequired: boolean) {
    const ctrl = this.claimForm.get(field);
    isRequired ? ctrl?.setValidators([Validators.required]) : ctrl?.clearValidators();
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  loadClaimTypes() {
    if (!this.claimForm) return;

    this.claimTypes = (this.catalogService.processes() || [])
      .filter((p: any) => String(p.category || '').toUpperCase() === 'CLAIM')
      .filter((p: any) => p.isActive !== false && p.processCategory?.isActive !== false)
      .map((p: any) => ({ id: p.slug, name: p.name }));

    const control = this.claimForm.get('claimType');
    if (this.claimTypes.length && !control?.value) {
      control?.setValue(this.claimTypes[0].id);
    }
  }

  private _prefillFromInputs() {
    if (this.vehiclesInput()?.length) {
      this.claimForm.get('vehicleId')?.setValue(this.vehiclesInput()![0].uuid);
    }
  }

  get currentStepIndex(): number {
    if (this.isThirdPartyClaim) return this.claimStep;
    return this.claimStep === this.STEP_CLAIM ? 2 : 1;
  }

  nextClaimStep() {
    if (this.claimStep === this.STEP_PARTY) {
      this.handleStep1Next();
      this.emitHeaderAction();
      return;
    }
    if (this.claimStep === this.STEP_THIRD_PARTY) {
      this.handleStep2Next();
      this.emitHeaderAction();
    }
  }

  allDetailsFinalized(): boolean {
    return areAllDetailsFinalized(this.claimDetails);
  }

  pendingDetailsCount(): number {
    return countPendingDetails(this.claimDetails);
  }

  submitBlockedReason(): string {
    if (this.isSaving()) return '';
    if (!this.claimDetails.length) return 'Registre al menos un daño.';
    const pending = this.pendingDetailsCount();
    if (pending > 0) {
      return `Finalice ${pending} daño(s) pendiente(s) para enviar la solicitud.`;
    }
    return '';
  }

  getDetailProgress(det: any) {
    return getDetailEvidenceProgress(det, this.createdClaim?.documents);
  }

  handleStep1Next() {
    const fields = ['vehicleId', 'claimType'];
    const invalid = fields.filter(f => this.claimForm.get(f)?.invalid);
    invalid.forEach(f => this.claimForm.get(f)?.markAsTouched());
    if (invalid.length) {
      this.Toast.fire({ icon: 'warning', title: `Faltan campos obligatorios` });
      return;
    }
    this.claimStep = this.isThirdPartyClaim ? this.STEP_THIRD_PARTY : this.STEP_CLAIM;
    if (this.claimStep === this.STEP_CLAIM) this.maybeOpenFirstDamage();
  }

  handleStep2Next() {
    const fields = ['thirdPartyDriverName', 'thirdPartyOwnerName', 'thirdPartyLicense', 'thirdPartyPhone', 'thirdPartyPlate', 'thirdPartyYear', 'thirdPartyBrandCode', 'thirdPartyModelCode'];
    const invalid = fields.filter(f => this.claimForm.get(f)?.invalid);
    invalid.forEach(f => this.claimForm.get(f)?.markAsTouched());
    if (invalid.length) {
      this.Toast.fire({ icon: 'warning', title: `Faltan campos del tercero afectado` });
      return;
    }
    this.claimStep = this.STEP_CLAIM;
    this.maybeOpenFirstDamage();
  }

  prevClaimStep() {
    if (this.claimStep === this.STEP_CLAIM) {
      this.claimStep = this.isThirdPartyClaim ? this.STEP_THIRD_PARTY : this.STEP_PARTY;
    } else if (this.claimStep === this.STEP_THIRD_PARTY) {
      this.claimStep = this.STEP_PARTY;
    }
    this.emitHeaderAction();
  }

  goToStep(step: number) {
    if (step < this.claimStep) return this.claimStep = step;
    if (this.claimStep === this.STEP_PARTY) return this.handleStep1Next();
    if (this.claimStep === this.STEP_THIRD_PARTY && step > this.STEP_THIRD_PARTY) return this.handleStep2Next();
  }

  onTimelineStepClick(stepId: number) {
    // Sin tercero el timeline solo muestra 2 pasos, así que su segundo hito es el paso de siniestro
    const targetStep = !this.isThirdPartyClaim && stepId === 2 ? this.STEP_CLAIM : stepId;
    this.goToStep(targetStep);
  }

  onClaimPartsSelected(parts: string[]) {
    this.partsSelected = parts;
  }

  // Valida campos globales del paso 2 y existencia de detalles
  private _validateGlobalStep2Fields(): boolean {
    const hasDetails = this.claimDetails.length > 0;
    if (!hasDetails) {
      this.Toast.fire({ icon: 'warning', title: 'Debe agregar al menos un detalle de daño.' });
    }
    return hasDetails;
  }

  saveClaimProgress(isSilent = false) {
    if (!this._validateGlobalStep2Fields()) return;
    try { this.capturedImage3D = this.claimFormComponent?.captureImage3D() ?? null; }
    catch { this.capturedImage3D = null; }
    if (!isSilent) this.isSaving.set(true);
    this._persistClaim(this._buildClaimPayload(), isSilent);
  }

  // Un siniestro ya creado en esta sesión se actualiza, nunca se vuelve a crear
  private _persistClaim(payload: any, isSilent: boolean) {
    const uuid = this.createdClaim?.uuid ?? this.claimUuidInput();
    const request$ = uuid
      ? this.addClaimService.updateClaim(uuid, payload)
      : this.addClaimService.addClaim(payload);
    request$.subscribe({
      next: (claim) => this._onClaimSaved(claim, isSilent),
      error: (err) => this._onSaveError(err)
    });
  }

  private _onClaimSaved(claim: any, isSilent = false) {
    this.isSaving.set(false);
    this.createdClaim = claim;
    this.claimDetails = claim.details ?? [];
    this.claimStep = this.STEP_CLAIM;
    if (!isSilent) this.Toast.fire({ icon: 'success', title: 'Progreso guardado.' });
    this.replaceNuevoClaimUrl(claim?.uuid);
  }

  private replaceNuevoClaimUrl(claimUuid?: string) {
    if (!claimUuid) return;
    const { partyUuid } = this.claimNavIds();
    if (!partyUuid) return;
    const st = this.md.parse();
    if (st.area !== 'socios' || st.view !== 'siniestro') return;
    if (st.claimUuid && st.claimUuid !== 'nuevo') return;
    const cmds = st.dano
      ? this.md.damage(partyUuid, claimUuid, st.detailUuid)
      : this.md.claim(partyUuid, claimUuid);
    this.md.replace(cmds);
  }

  onDetailsChanged(newList: any[]) {
    this.claimDetails = newList;
    this.saveClaimProgress(true);
  }

  private _onSaveError(err: any) {
    this.isSaving.set(false);
    Swal.fire('Error', err.error?.message ?? 'Error al guardar.', 'error');
  }

  // Construye el payload completo del siniestro
  private _buildClaimPayload() {
    const val = this.claimForm.getRawValue();
    return {
      ...this._assembleBasePayload(val),
      details: this._mapDetailsPayload(),
      thirdPartyInfo: val.claimType === 'ACCIDENT_THIRD_PARTY' ? this._buildThirdPartyPayload(val) : undefined
    };
  }

  private _mapDetailsPayload() {
    return this.claimDetails.map(d => ({
      id: d.id, uuid: d.uuid,
      description: d.description, damageDescription: d.damageDescription,
      laborCost: sanitizeNumericValue(d.laborCost), partsCost: sanitizeNumericValue(d.partsCost), totalCost: sanitizeNumericValue(d.totalCost),
      affectedParts: d.affectedParts, isNegligence: !!d.isNegligence, deductible: sanitizeNumericValue(d.deductible),
      dateOfLoss: d.dateOfLoss,
      driverName: d.driverName, driverLicense: d.driverLicense, driverLicenseClass: d.driverLicenseClass,
      geographicLocation: d.geographicLocation,
      latitude: d.latitude ?? null,
      longitude: d.longitude ?? null
    }));
  }

  // Une el socio y vehiculo con datos del primer daño como cabecera
  private _assembleBasePayload(val: any) {
    const firstDetail = this.claimDetails[0];
    return {
      partyId: this.partyInput()?.uuid, vehicleId: val.vehicleId,
      claimType: val.claimType, dateOfLoss: firstDetail?.dateOfLoss ?? val.date,
      parts: this.partsSelected, image3D: this.capturedImage3D,
      ...this._getClaimDriverAndCostsPayload(val)
    };
  }

  // Mapea los costos y conductor usando el primer daño como fallback
  private _getClaimDriverAndCostsPayload(val: any) {
    const firstDetail = this.claimDetails[0];
    return {
      driverName: firstDetail?.driverName ?? null, driverLicense: firstDetail?.driverLicense ?? null,
      driverLicenseClass: firstDetail?.driverLicenseClass ?? null, accessoriesDescription: val.accessoriesDescription,
      geographicLocation: 'Ubicación Siniestro', laborCost: sanitizeNumericValue(val.laborCost),
      partsCost: sanitizeNumericValue(val.partsCost), totalCost: sanitizeNumericValue(val.totalCost),
      outOfCourtPayment: sanitizeNumericValue(val.outOfCourtPayment)
    };
  }


  private _buildThirdPartyPayload(val: any) {
    return {
      thirdPartyDriverName: val.thirdPartyDriverName, thirdPartyOwnerName: val.thirdPartyOwnerName,
      thirdPartyLicense: val.thirdPartyLicense, thirdPartyLicenseClass: val.thirdPartyLicenseClass,
      thirdPartyPhone: val.thirdPartyPhone, plate: val.thirdPartyPlate,
      brandCode: val.thirdPartyBrandCode, modelCode: val.thirdPartyModelCode,
      year: val.thirdPartyYear,
      hasInsurance: val.thirdPartyHasInsurance ?? false, tookResponsibility: val.thirdPartyTookResponsibility ?? false
    };
  }



  async submitClaimDocuments() {
    if (this.isSaving()) return;
    if (!this.allDetailsFinalized()) {
      this.Toast.fire({ icon: 'warning', title: this.submitBlockedReason() || 'Complete los daños antes de enviar.' });
      return;
    }
    if (!(await this._confirmReportTimeframe())) return;
    this.isSaving.set(true);
    const uploads = this._assembleUploadsList();
    if (uploads.length === 0) {
      this._submitClaimAndClose();
      return;
    }
    const optimized = await this._optimizeUploads(uploads);
    this._uploadClaimFiles(optimized);
  }

  /** Art. VII.1 — plazo de informe (60 días) al enviar la solicitud. */
  private async _confirmReportTimeframe(): Promise<boolean> {
    const dates = this.claimDetails
      .map((d) => d.dateOfLoss)
      .filter(Boolean)
      .sort();
    const oldest = dates[0];
    const diff = daysSince(oldest);
    if (diff == null) return true;
    const processSlug = this.claimForm.get('claimType')?.value || null;
    const maxDays = reportMaxDays(this.catalogService, processSlug);
    if (diff <= maxDays) return true;
    const res = await Swal.fire({
      icon: 'warning',
      title: 'Plazo de informe superado',
      text: `Han transcurrido ${diff} días desde el daño más antiguo (límite de informe: ${maxDays} días, Art. VII.1). ¿Desea enviar de todos modos?`,
      showCancelButton: true,
      confirmButtonText: 'Enviar de todos modos',
      cancelButtonText: 'Cancelar'
    });
    return !!res.isConfirmed;
  }

  /** Disparado por app-bpm (Enviar solicitud). */
  onBpmApprove(): void {
    void this.submitClaimDocuments();
  }

  onBpmConfirmed(_payload: StepApprovalPayload): void {
    void this.submitClaimDocuments();
  }

  private _assembleUploadsList() {
    const uploads: { uuid: string; file: File }[] = [];
    this._addDetailsUploads(uploads);
    return uploads;
  }

  private _addDetailsUploads(uploads: any[]) {
    const docs = this.createdClaim?.documents ?? [];
    const details = this.createdClaim?.details ?? [];
    this.claimDetails.forEach((ld, idx) => {
      const db = details[idx] || details.find((d: any) => d.description === ld.description);
      const doc = db ? docs.find((d: any) => d.claimDetailId === db.id && d.typeSlug === 'QUOTE_1') : null;
      if (ld.file && doc?.uuid) uploads.push({ uuid: doc.uuid, file: ld.file });
    });
  }

  private async _optimizeUploads(uploads: any[]) {
    Swal.fire({ title: 'Optimizando...', text: 'Preparando archivos para el servidor', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    return await Promise.all(uploads.map(async u => ({
      uuid: u.uuid,
      file: await this.imageOptimizer.optimize(u.file)
    })));
  }

  private _uploadClaimFiles(uploads: { uuid: string; file: File }[]) {
    Swal.fire({ title: 'Subiendo documentos...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const obs = uploads.map(u => this.addClaimService.uploadClaimDocument(u.uuid, u.file));
    forkJoin(obs).subscribe({
      next: () => this._submitClaimAndClose(),
      error: (err) => {
        console.error(err);
        this._submitClaimAndClose('El siniestro se registró, pero algunos documentos no se subieron.');
      }
    });
  }

  private _submitClaimAndClose(uploadWarning?: string) {
    Swal.fire({ title: 'Enviando solicitud...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.addClaimService.submitClaim(this.createdClaim.uuid).subscribe({
      next: (claim: any) => this._closeAfterSubmit(
        uploadWarning
          ? { icon: 'warning', title: 'Siniestro registrado', text: uploadWarning }
          : { icon: 'success', title: 'Siniestro registrado', text: 'El siniestro fue enviado al flujo de aprobación.' },
        claim?.request?.uuid ?? claim?.requestUuid ?? this.createdClaim?.request?.uuid
      ),
      error: (err) => {
        console.error(err);
        this._closeAfterSubmit({
          icon: 'error',
          title: 'Error',
          text: err.error?.message || 'Siniestro creado, pero falló el envío al flujo de aprobación.'
        });
      }
    });
  }

  // Cierra el flujo notificando mediante Toast no invasivo
  private _closeAfterSubmit(
    alert: { icon: 'success' | 'warning' | 'error'; title: string; text: string },
    requestUuid?: string | null
  ) {
    this.isSaving.set(false);
    Swal.close();
    this.Toast.fire({ icon: alert.icon, title: alert.title, text: alert.text });
    this.claimAdded.emit();
    this.onClose.emit(true);
    if (alert.icon !== 'error' && requestUuid) this.md.go(this.md.request(requestUuid));
  }

  refreshClaimData() {
    if (!this.createdClaim?.uuid) return;
    this.addClaimService.getClaim(this.createdClaim.uuid).subscribe({
      next: (claim) => {
        this.createdClaim = claim;
        this.claimDetails = claim.details ?? [];
        this.cdr.markForCheck();
      }
    });
  }

}

